/**
 * AgentWorkspaceView — Live browser/desktop workspace panel.
 *
 * Shows:
 *  - Live/latest browser screenshot
 *  - Current URL + page title
 *  - Current session status
 *  - Buttons: Refresh, Open artifact, Pause task, Resume task, Approve pending
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Descriptions, Empty, Image, Space, Spin,
  Tag, Timeline, Tooltip, Typography, message,
} from 'antd';
import {
  CameraOutlined, DesktopOutlined, GlobalOutlined, SearchOutlined,
  PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import * as api from '../api/client';

const { Text, Title } = Typography;

// ── Types ─────────────────────────────────────────────────────────────────────

interface BrowserSession {
  session_id: string;
  task_id: string | null;
  step_id: string | null;
  workspace_id: string | null;
  current_url: string;
  current_title: string;
  latest_screenshot_b64: string | null;
  latest_screenshot_path: string | null;
  video_path: string | null;
  stream_mode?: string;
  live_url?: string | null;
  created_at: string;
  updated_at: string;
  status: string;
  actions_history: HistoryEntry[];
}

interface HistoryEntry {
  timestamp: string;
  action: string;
  url?: string;
  selector?: string;
  title?: string;
  query?: string;
  search_engine?: string;
  target_url?: string;
  value?: string;
  screenshot_b64?: string;
  error?: string;
}

interface Props {
  taskId: string;
  onPause?: () => void;
  onResume?: () => void;
  onApprove?: () => void;
  pendingApprovals?: number;
  taskStatus?: string;
}

// ── Status badge helper ───────────────────────────────────────────────────────

const SESSION_STATUS_COLOR: Record<string, string> = {
  active: 'green',
  stopped: 'default',
  error: 'red',
};

// ── Component ─────────────────────────────────────────────────────────────────

const AgentWorkspaceView: React.FC<Props> = ({
  taskId,
  onPause,
  onResume,
  onApprove,
  pendingApprovals = 0,
  taskStatus,
}) => {
  const [sessions, setSessions] = useState<BrowserSession[]>([]);
  const [activeSession, setActiveSession] = useState<BrowserSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string>('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch sessions for this task ───────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.listDesktopSessions();
      setWorkspaceError('');
      const all: BrowserSession[] = res.data?.sessions ?? [];
      const forTask = all.filter(s => s.task_id === taskId || !taskId);
      setSessions(forTask);

      // Prefer the most recently updated active session
      const active = forTask
        .filter(s => s.status === 'active')
        .sort((a, b) => (b.updated_at > a.updated_at ? 1 : -1))[0] ?? null;

      // If we have an active session, fetch its full detail (has latest screenshot)
      if (active?.session_id) {
        const detail = await api.getDesktopSession(active.session_id);
        setActiveSession(detail.data ?? active);
      } else {
        setActiveSession(forTask.length > 0 ? forTask[0] : null);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      setWorkspaceError(
        status
          ? `Browser workspace endpoint returned ${status}${detail ? `: ${detail}` : ''}`
          : `Browser workspace endpoint is unavailable${detail ? `: ${detail}` : ''}`,
      );
    }
  }, [taskId]);

  useEffect(() => {
    setLoading(true);
    fetchSessions().finally(() => setLoading(false));
  }, [fetchSessions]);

  // Poll rapidly while task is running so browser screenshots feel live.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const isRunning = taskStatus && ['RUNNING', 'PLANNING'].includes(taskStatus);
    if (isRunning) {
      pollRef.current = setInterval(fetchSessions, 1250);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [taskStatus, fetchSessions]);

  // ── Manual refresh ─────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    if (!activeSession) return;
    setRefreshing(true);
    try {
      const res = await api.getLatestScreenshot(activeSession.session_id);
      const data = res.data;
      setActiveSession(prev =>
        prev
          ? {
              ...prev,
              latest_screenshot_b64: data.screenshot_b64 ?? prev.latest_screenshot_b64,
              current_url: data.current_url ?? prev.current_url,
              current_title: data.current_title ?? prev.current_title,
              updated_at: data.updated_at ?? prev.updated_at,
            }
          : prev,
      );
    } catch {
      message.warning('Could not refresh screenshot');
    } finally {
      setRefreshing(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const screenshotSrc = activeSession?.latest_screenshot_b64
    ? `data:image/png;base64,${activeSession.latest_screenshot_b64}`
    : null;

  const recentHistory = (activeSession?.actions_history ?? []).slice(-16).reverse();
  const currentAction = recentHistory[0];

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Spin tip="Loading workspace…" />
      </div>
    );
  }

  // ── No sessions ────────────────────────────────────────────────────────────
  if (!activeSession) {
    return (
      <div style={{ padding: '24px 16px' }}>
        {workspaceError && (
          <Alert
            type="warning"
            showIcon
            message="Browser workspace is not reachable"
            description={workspaceError}
            style={{ marginBottom: 16 }}
          />
        )}
        <Empty
          image={<DesktopOutlined style={{ fontSize: 48, color: '#30363d' }} />}
          description={
            <span style={{ color: '#555', fontSize: 13 }}>
              No browser session for this task yet.
              <br />
              Browser sessions start automatically when the agent uses browser tools.
            </span>
          }
        />
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: 620 }}>

      {/* ── Action bar ─────────────────────────────────────────────────────── */}
      <Space wrap style={{ marginBottom: 12 }}>
        <Tag color="cyan" icon={<DesktopOutlined />}>Visual Browser</Tag>
        {currentAction && (
          <Tag color="blue" icon={currentAction.action?.includes('search') ? <SearchOutlined /> : <CameraOutlined />}>
            {currentAction.action.replace(/_/g, ' ')}
          </Tag>
        )}
        <Tooltip title="Refresh screenshot">
          <Button
            size="small"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </Tooltip>

        {onPause && (
          <Tooltip title="Pause task">
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={onPause}
              disabled={taskStatus !== 'RUNNING'}
            >
              Pause
            </Button>
          </Tooltip>
        )}

        {onResume && (
          <Tooltip title="Resume task">
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={onResume}
              disabled={taskStatus !== 'PAUSED'}
            >
              Resume
            </Button>
          </Tooltip>
        )}

        {onApprove && pendingApprovals > 0 && (
          <Button
            size="small"
            type="primary"
            danger
            onClick={onApprove}
          >
            {pendingApprovals} Approval{pendingApprovals > 1 ? 's' : ''} Pending
          </Button>
        )}

        {/* Session count badge */}
        {sessions.length > 1 && (
          <Tag color="blue">{sessions.length} sessions</Tag>
        )}
      </Space>

      {/* ── Session info ───────────────────────────────────────────────────── */}
      <Card
        size="small"
        style={{ background: '#161b22', borderColor: '#1e2736', marginBottom: 10 }}
        bodyStyle={{ padding: '8px 12px' }}
      >
        <Descriptions size="small" column={1} labelStyle={{ color: '#555', fontSize: 11 }}>
          <Descriptions.Item label="Status">
            <Badge
              status={activeSession.status === 'active' ? 'processing' : 'default'}
              color={SESSION_STATUS_COLOR[activeSession.status] ?? 'default'}
              text={
                <Tag
                  color={SESSION_STATUS_COLOR[activeSession.status] ?? 'default'}
                  style={{ fontSize: 10 }}
                >
                  {activeSession.status.toUpperCase()}
                </Tag>
              }
            />
          </Descriptions.Item>

          <Descriptions.Item label={<span><GlobalOutlined /> URL</span>}>
            <Text
              copyable
              style={{ color: '#79c0ff', fontSize: 11, fontFamily: 'monospace' }}
              ellipsis={{ tooltip: activeSession.current_url }}
            >
              {activeSession.current_url || '—'}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Title">
            <Text style={{ color: '#e6edf3', fontSize: 11 }} ellipsis>
              {activeSession.current_title || '—'}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Session ID">
            <Text style={{ color: '#555', fontSize: 10, fontFamily: 'monospace' }}>
              {activeSession.session_id}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="Updated">
            <Text style={{ color: '#555', fontSize: 10 }}>
              {activeSession.updated_at
                ? new Date(activeSession.updated_at).toLocaleTimeString()
                : '—'}
            </Text>
          </Descriptions.Item>
          {currentAction && (
            <Descriptions.Item label="Current action">
              <Text style={{ color: '#e6edf3', fontSize: 11 }}>
                {currentAction.action.replace(/_/g, ' ')}
                {currentAction.query ? `: ${currentAction.query}` : ''}
                {currentAction.selector ? ` (${currentAction.selector})` : ''}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* ── Live screenshot ────────────────────────────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <CameraOutlined style={{ color: '#1890ff' }} />
            <Text style={{ color: '#8b949e', fontSize: 12 }}>Live Browser View</Text>
            {taskStatus === 'RUNNING' && (
              <Badge status="processing" text={<span style={{ color: '#8b949e', fontSize: 11 }}>updating</span>} />
            )}
          </Space>
        }
        style={{ background: '#161b22', borderColor: '#1e2736', marginBottom: 10 }}
        bodyStyle={{ padding: 4 }}
      >
        {screenshotSrc ? (
          <Image
            src={screenshotSrc}
            alt="Browser screenshot"
            style={{ width: '100%', borderRadius: 6, background: '#0d1117' }}
            preview={{ mask: 'Click to expand' }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: '#555', fontSize: 11 }}>No screenshot yet</span>}
            style={{ margin: '12px 0' }}
          />
        )}
      </Card>

      {/* ── Screenshot history ─────────────────────────────────────────────── */}
      {recentHistory.length > 0 && (
        <Card
          size="small"
          title={<Text style={{ color: '#8b949e', fontSize: 12 }}>Browser Actions</Text>}
          style={{ background: '#161b22', borderColor: '#1e2736', marginBottom: 10 }}
          bodyStyle={{ padding: '8px 12px' }}
        >
          <Timeline style={{ marginTop: 8 }}>
            {recentHistory.map(h => (
              <Timeline.Item key={`${h.timestamp}-${h.action}`} color={h.error ? 'red' : 'blue'}>
                <div>
                  <Text style={{ color: '#c9d1d9', fontSize: 11, textTransform: 'capitalize' }}>
                    {h.action.replace(/_/g, ' ')}
                  </Text>
                  <Text style={{ color: '#555', fontSize: 10, marginLeft: 8 }}>
                    {new Date(h.timestamp).toLocaleTimeString()}
                  </Text>
                  {(h.query || h.value || h.selector) && (
                    <Text style={{ color: '#8b949e', fontSize: 10, display: 'block' }} ellipsis>
                      {h.query || h.value || h.selector}
                    </Text>
                  )}
                  {h.url && (
                    <Text style={{ color: '#79c0ff', fontSize: 10, display: 'block', fontFamily: 'monospace' }} ellipsis>
                      {h.url}
                    </Text>
                  )}
                  {h.error && (
                    <Text style={{ color: '#f85149', fontSize: 10, display: 'block' }}>
                      {h.error}
                    </Text>
                  )}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      )}

    </div>
  );
};

export default AgentWorkspaceView;
